import React, { useState, useEffect } from 'react';
import { CategoryProposalModal } from './CategoryProposalModal';
import { config } from '../config';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import '../styles/CategorySelector.css';

interface CategoryNode {
    id: string;
    name: string;
    children?: CategoryNode[];
}

interface Taxonomy {
    version: string;
    taxonomy: CategoryNode[];
}

interface CategorySelectorProps {
    currentCategoryId?: string;
    itemId: string;
    onSave: (categoryId: string, categoryPath: string) => Promise<void>;
    onCancel: () => void;
    getAuthToken: () => Promise<string | null>;
}

export const CategorySelector: React.FC<CategorySelectorProps> = ({
    currentCategoryId,
    itemId,
    onSave,
    onCancel,
    getAuthToken
}) => {
    const [taxonomy, setTaxonomy] = useState<CategoryNode[]>([]);
    const [level1Selected, setLevel1Selected] = useState<string>('');
    const [level2Selected, setLevel2Selected] = useState<string>('');
    const [level3Selected, setLevel3Selected] = useState<string>('');
    const [level4Selected, setLevel4Selected] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showProposalModal, setShowProposalModal] = useState(false);
    const [proposalSuccess, setProposalSuccess] = useState(false);

    // Load taxonomy on mount
    useEffect(() => {
        fetch('/taxonomy.sk.json')
            .then(res => res.json())
            .then((data: Taxonomy) => {
                setTaxonomy(data.taxonomy);

                // Pre-select current category if provided
                if (currentCategoryId) {
                    preselectCategory(currentCategoryId, data.taxonomy);
                }
            })
            .catch(err => {
                console.error('Failed to load taxonomy:', err);
                setError('Nepodarilo sa načítať kategórie');
            });
    }, [currentCategoryId]);

    // Pre-select category levels based on current category ID
    const preselectCategory = (categoryId: string, taxonomyData: CategoryNode[]) => {
        const parts = categoryId.split('.');

        if (parts.length >= 1) {
            setLevel1Selected(parts[0]);
        }
        if (parts.length >= 2) {
            setLevel2Selected(parts.slice(0, 2).join('.'));
        }
        if (parts.length >= 3) {
            setLevel3Selected(parts.slice(0, 3).join('.'));
        }
        if (parts.length >= 4) {
            setLevel4Selected(categoryId);
        }
    };

    // Get available options for each level
    const getLevel1Options = (): CategoryNode[] => {
        return taxonomy;
    };

    const getLevel2Options = (): CategoryNode[] => {
        if (!level1Selected) return [];
        const level1Node = taxonomy.find(n => n.id === level1Selected);
        return level1Node?.children || [];
    };

    const getLevel3Options = (): CategoryNode[] => {
        if (!level2Selected) return [];
        const level1Node = taxonomy.find(n => n.id === level1Selected);
        const level2Node = level1Node?.children?.find(n => n.id === level2Selected);
        return level2Node?.children || [];
    };

    const getLevel4Options = (): CategoryNode[] => {
        if (!level3Selected) return [];
        const level1Node = taxonomy.find(n => n.id === level1Selected);
        const level2Node = level1Node?.children?.find(n => n.id === level2Selected);
        const level3Node = level2Node?.children?.find(n => n.id === level3Selected);
        return level3Node?.children || [];
    };

    // Handle level selection changes
    const handleLevel1Change = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setLevel1Selected(e.target.value);
        setLevel2Selected('');
        setLevel3Selected('');
        setLevel4Selected('');
    };

    const handleLevel2Change = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setLevel2Selected(e.target.value);
        setLevel3Selected('');
        setLevel4Selected('');
    };

    const handleLevel3Change = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setLevel3Selected(e.target.value);
        setLevel4Selected('');
    };

    const handleLevel4Change = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setLevel4Selected(e.target.value);
    };

    // Build category path from selected levels
    const buildCategoryPath = (categoryId: string): string => {
        const parts = categoryId.split('.');
        const pathNames: string[] = [];

        function findNode(nodes: CategoryNode[], id: string): CategoryNode | null {
            for (const node of nodes) {
                if (node.id === id) return node;
                if (node.children) {
                    const found = findNode(node.children, id);
                    if (found) return found;
                }
            }
            return null;
        }

        for (let i = 1; i <= parts.length; i++) {
            const partialId = parts.slice(0, i).join('.');
            const node = findNode(taxonomy, partialId);
            if (node) {
                pathNames.push(node.name);
            }
        }

        return pathNames.join(' > ');
    };

    // Get the most specific selected category
    const getSelectedCategoryId = (): string | null => {
        if (level4Selected) return level4Selected;
        if (level3Selected) return level3Selected;
        if (level2Selected) return level2Selected;
        if (level1Selected) return level1Selected;
        return null;
    };

    // Handle save
    const handleSave = async () => {
        const categoryId = getSelectedCategoryId();
        if (!categoryId) {
            setError('Prosím, vyber kategóriu');
            return;
        }

        const categoryPath = buildCategoryPath(categoryId);
        setLoading(true);
        setError(null);

        try {
            await onSave(categoryId, categoryPath);
        } catch (err: any) {
            setError(err.message || 'Nepodarilo sa uložiť kategóriu');
            setLoading(false);
        }
    };

    // Handle category proposal submission
    const handleProposalSubmit = async (proposedCategory: string, additionalNote: string) => {
        const token = await getAuthToken();
        if (!token) {
            throw new Error('Nie ste prihlásený');
        }

        const response = await fetchWithRetry(`${config.apiUrl}/proposeCategoryAddition`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Auth-Token': `Bearer ${token}`
            },
            credentials: 'include',
            body: JSON.stringify({
                item_id: itemId,
                proposed_category_name: proposedCategory,
                additional_note: additionalNote
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Nepodarilo sa odoslať návrh');
        }

        // Show success message
        setProposalSuccess(true);
        setTimeout(() => setProposalSuccess(false), 5000);
    };

    const level2Options = getLevel2Options();
    const level3Options = getLevel3Options();
    const level4Options = getLevel4Options();

    return (
        <div className="category-selector">
            <div className="category-selector-header">
                <h3>Upraviť kategóriu</h3>
            </div>

            <div className="category-selector-info">
                <p>
                    ℹ️ Zmena kategórie tejto položky sa prejaví aj na všetkých tvojich uložených bločkoch
                    a uplatní sa pri budúcich bločkoch (ak bude na bločku rovnaký názov položky).
                </p>
            </div>

            {error && (
                <div className="category-selector-error">
                    {error}
                </div>
            )}

            {proposalSuccess && (
                <div className="category-selector-success">
                    ✓ Návrh bol úspešne odoslaný! Ďakujeme.
                </div>
            )}

            <div className="category-selector-dropdowns">
                {/* Level 1 */}
                <div className="category-dropdown">
                    <label>Hlavná kategória</label>
                    <select
                        value={level1Selected}
                        onChange={handleLevel1Change}
                        disabled={loading}
                    >
                        <option value="">-- Vyber kategóriu --</option>
                        {getLevel1Options().map(cat => (
                            <option key={cat.id} value={cat.id}>
                                {cat.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Level 2 */}
                {level1Selected && level2Options.length > 0 && (
                    <div className="category-dropdown">
                        <label>Podkategória <span style={{ color: '#999', fontWeight: 'normal' }}>(voliteľné)</span></label>
                        <select
                            value={level2Selected}
                            onChange={handleLevel2Change}
                            disabled={loading}
                        >
                            <option value="">----</option>
                            {level2Options.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Level 3 */}
                {level2Selected && level3Options.length > 0 && (
                    <div className="category-dropdown">
                        <label>Detailná kategória <span style={{ color: '#999', fontWeight: 'normal' }}>(voliteľné)</span></label>
                        <select
                            value={level3Selected}
                            onChange={handleLevel3Change}
                            disabled={loading}
                        >
                            <option value="">----</option>
                            {level3Options.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Level 4 */}
                {level3Selected && level4Options.length > 0 && (
                    <div className="category-dropdown">
                        <label>Špecifická kategória <span style={{ color: '#999', fontWeight: 'normal' }}>(voliteľné)</span></label>
                        <select
                            value={level4Selected}
                            onChange={handleLevel4Change}
                            disabled={loading}
                        >
                            <option value="">----</option>
                            {level4Options.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <div className="category-selector-footer">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        setShowProposalModal(true);
                    }}
                    className="category-missing-link"
                >
                    Chýba ti nejaká kategória?
                </a>
            </div>

            <div className="category-selector-actions">
                <button
                    onClick={onCancel}
                    disabled={loading}
                    className="btn-cancel"
                >
                    Zrušiť
                </button>
                <button
                    onClick={handleSave}
                    disabled={loading || !getSelectedCategoryId()}
                    className="btn-save"
                >
                    {loading ? 'Ukladám...' : 'Uložiť'}
                </button>
            </div>

            {/* Category Proposal Modal */}
            {showProposalModal && (
                <CategoryProposalModal
                    onClose={() => setShowProposalModal(false)}
                    onSubmit={handleProposalSubmit}
                />
            )}
        </div>
    );
};
